import MutateEVSEAndConnectorsForm, {
  MutateEVSEAndConnectorsFormValues,
} from '../../ui/places/MutateEVSEAndConnectorsForm.tsx';
import { UpdateEVSEMetadataRequest } from '../../../api/requests/evse-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import useUpdateEVSEMetadata from '../../../hooks/evses/useUpdateEVSEMetadata.ts';

import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import useEVSE from '../../../hooks/evses/useEVSE.ts';
import { LoadingOverlay, Modal } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErrorResponse } from '../../../api/responses/error-response.ts';

interface UpdateEVSEAndConnectorsModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  evseId: string;
  onSuccess?: () => void;
}

const UpdateEVSEAndConnectorsModal = ({
  opened,
  onClose,
  serviceId,
  evseId,
  onSuccess,
}: UpdateEVSEAndConnectorsModalProps) => {
  const queryClient = useQueryClient();
  const { data: evse, isLoading: isEVSELoading } = useEVSE(evseId);

  const { mutateAsync: updateEVSEMetadata } = useUpdateEVSEMetadata();

  const { mutate: handleSubmit, isPending: isUpdateEVSEMetadataPending } =
    useMutation<void, ErrorResponse, MutateEVSEAndConnectorsFormValues>({
      mutationFn: async (formValues) => {
        const updateEVSEMetadataRequest: UpdateEVSEMetadataRequest = {
          evseId: evseId,
          floorLevels: formValues.evseFloorLevel,
          evseSerialNumber: formValues.evseSerialNumber,
        };

        await updateEVSEMetadata(updateEVSEMetadataRequest);
      },
      onSuccess: async () => {
        notifySuccess('更新充電資訊成功');
        await queryClient.invalidateQueries({
          queryKey: ['evses', evseId],
          exact: true,
        });
        onSuccess?.();
      },
      onError: (error) => {
        notifyError('更新充電資訊失敗', error.message);
      },
    });

  const handleClose = () => {
    onClose();
  };

  if (isEVSELoading) return <LoadingOverlay />;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      title="新增充電資訊 - 充電槍"
      centered
      size={659}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      <MutateEVSEAndConnectorsForm
        initialValues={{
          serviceId: serviceId,
          isEVSEAlreadyCreatedBefore: true,
          isEVSENewlyCreatedDuringThisProcess: false,
          evseId: evseId,
          evseSerialNumber: evse.evseSerialNumber,
          evseFloorLevel: evse.floorLevels,
          chargingConnectors: Object.values(evse.connectors),
        }}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        isSubmitting={isUpdateEVSEMetadataPending}
        submitButtonText={'更新'}
      />
    </Modal>
  );
};

export { UpdateEVSEAndConnectorsModal };
